'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useReducer } from 'react';
export const FavoritesContext = createContext(null);
function favoritesReducer(state, action) {
    switch (action.type) {
        case 'ADD_FAVORITE': {
            const existingItem = state.items.find((item) => item === action.payload);
            if (existingItem) {
                return state;
            }
            const newItems = [...state.items, action.payload];
            return { items: newItems };
        }
        case 'REMOVE_FAVORITE': {
            const newItems = state.items.filter((item) => item !== action.payload);
            return { items: newItems };
        }
        case 'SYNC_FAVORITES': {
            return { items: action.payload };
        }
        default:
            return state;
    }
}
export function FavoritesProvider({ children }) {
    // Load initial favorites from localStorage
    const getInitialState = () => {
        if (typeof globalThis.window !== 'undefined') {
            const stored = globalThis.window.localStorage.getItem('favorites');
            if (stored) {
                try {
                    return { items: JSON.parse(stored) };
                }
                catch (_a) {
                    return { items: [] };
                }
            }
        }
        return { items: [] };
    };
    const [state, dispatch] = useReducer(favoritesReducer, undefined, getInitialState);
    // Persist favorites to localStorage whenever they change
    React.useEffect(() => {
        if (typeof globalThis.window !== 'undefined') {
            globalThis.window.localStorage.setItem('favorites', JSON.stringify(state.items));
        }
    }, [state.items]);
    const addFavorite = (productSku) => {
        dispatch({ type: 'ADD_FAVORITE', payload: productSku });
    };
    const removeFavorite = (productSku) => {
        dispatch({ type: 'REMOVE_FAVORITE', payload: productSku });
    };
    const syncFromStorage = React.useCallback(() => {
        if (globalThis.window !== undefined) {
            const stored = globalThis.window.localStorage.getItem('favorites');
            try {
                dispatch({ type: 'SYNC_FAVORITES', payload: stored ? JSON.parse(stored) : [] });
            }
            catch (_a) {
                dispatch({ type: 'SYNC_FAVORITES', payload: [] });
            }
        }
    }, [dispatch]);
    const toggleFavorite = (productSku) => {
        const isCurrentlyFavorite = state.items.includes(productSku);
        if (isCurrentlyFavorite) {
            removeFavorite(productSku);
        }
        else {
            addFavorite(productSku);
        }
    };
    const isFavorite = (productSku) => {
        return state.items.includes(productSku);
    };
    const contextValue = React.useMemo(() => ({
        state,
        dispatch,
        addFavorite,
        removeFavorite,
        toggleFavorite,
        isFavorite,
        syncFromStorage,
    }), 
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [state, dispatch, syncFromStorage]);
    return _jsx(FavoritesContext.Provider, { value: contextValue, children: children });
}
export function useFavorites() {
    const context = useContext(FavoritesContext);
    if (!context) {
        throw new Error('useFavorites must be used within a FavoritesProvider.');
    }
    return context;
}
